import type { Editor } from "@tiptap/react";
import type { ResponsiveColumnStyles } from "../types";
import { createDefaultColumnConfig } from "./tableConstants";
import { parseStyleString, rgbToHex, parsePaddingString } from "./styleUtils";

/**
 * Get the current column index from editor selection
 */
export const getCurrentColumnIndex = (editor: Editor | null): number => {
  if (!editor) return 0;
  const { selection } = editor.state;
  const { $from } = selection;
  
  let rowNode = null;
  let rowDepth = 0;
  let cellPos = 0;
  
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
      cellPos = $from.before(depth);
    }
    if (node.type.name === "tableRow") {
      rowNode = node;
      rowDepth = depth;
      break;
    }
  }
  
  if (!rowNode) return 0;
  
  const rowStart = $from.before(rowDepth);
  let colIndex = 0;
  let foundIndex = 0;
  
  rowNode.forEach((child, offset) => {
    if (child.type.name === "tableCell" || child.type.name === "tableHeader") {
      const childPos = rowStart + offset + 1;
      if (childPos === cellPos) {
        foundIndex = colIndex;
      }
      colIndex++;
    }
  });
  
  return foundIndex;
};

/**
 * Get total columns in the current table
 */
export const getTotalColumns = (editor: Editor | null): number => {
  if (!editor) return 3;
  
  const { selection } = editor.state;
  const { $from } = selection;
  
  // First try to find table from selection
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "tableRow") {
      let count = 0;
      node.forEach((child) => {
        if (child.type.name === "tableCell" || child.type.name === "tableHeader") {
          count++;
        }
      });
      return count;
    }
  }
  
  // Fallback: search the document for any table
  let columnCount = 3;
  editor.state.doc.descendants((node) => {
    if (node.type.name === "tableRow") {
      let count = 0;
      node.forEach((child) => {
        if (child.type.name === "tableCell" || child.type.name === "tableHeader") {
          count++;
        }
      });
      columnCount = count;
      return false; // Stop after first row
    }
    return true;
  });
  
  return columnCount;
};

/**
 * Get the current row index from editor selection
 */
export const getCurrentRowIndex = (editor: Editor | null): number => {
  if (!editor) return 0;
  const { selection } = editor.state;
  const { $from } = selection;
  
  let tableNode = null;
  let tableDepth = 0;
  let rowPos = 0;
  
  // Find the table and current row
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "tableRow") {
      rowPos = $from.before(depth);
    }
    if (node.type.name === "table") {
      tableNode = node;
      tableDepth = depth;
      break;
    }
  }
  
  if (!tableNode) return 0;
  
  const tableStart = $from.before(tableDepth);
  let rowIndex = 0;
  let foundIndex = 0;
  
  tableNode.forEach((child, offset) => {
    if (child.type.name === "tableRow") {
      const childPos = tableStart + offset + 1;
      if (childPos === rowPos) {
        foundIndex = rowIndex;
      }
      rowIndex++;
    }
  });
  
  return foundIndex;
};

/**
 * Get total rows in the current table
 */
export const getTotalRows = (editor: Editor | null): number => {
  if (!editor) return 3;
  
  const { selection } = editor.state;
  const { $from } = selection;
  
  // First try to find table from selection
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "table") {
      let count = 0;
      node.forEach((child) => {
        if (child.type.name === "tableRow") {
          count++;
        }
      });
      return count;
    }
  }
  
  // Fallback: search the document for any table
  let rowCount = 3;
  editor.state.doc.descendants((node) => {
    if (node.type.name === "table") {
      let count = 0;
      node.forEach((child) => {
        if (child.type.name === "tableRow") {
          count++;
        }
      });
      rowCount = count;
      return false; // Stop after first table
    }
    return true;
  });
  
  return rowCount;
};

/**
 * Read column settings from TipTap's document model or DOM
 */
export const readColumnSettingsFromDOM = (
  editor: Editor | null, 
  colIndex: number
): { columnIndex: number; styles: ResponsiveColumnStyles } => {
  const defaultColumnConfig = createDefaultColumnConfig();
  
  const createDefaultStyles = (): ResponsiveColumnStyles => ({
    xs: { ...defaultColumnConfig },
    sm: { ...defaultColumnConfig },
    md: { ...defaultColumnConfig },
    lg: { ...defaultColumnConfig },
    xl: { ...defaultColumnConfig },
  });

  const defaultSettings = {
    columnIndex: colIndex,
    styles: createDefaultStyles(),
  };

  // Helper to parse column width from "type:value" format
  const parseColWidth = (str: string | null | undefined): { widthType: "percent" | "pixel"; widthValue: string } => {
    if (!str) return { widthType: "percent", widthValue: "33" };
    const parts = str.split(":");
    const type = parts[0] as string;
    // Convert legacy "auto" to "percent"
    const widthType = (type === "auto" || !type) ? "percent" : type as "percent" | "pixel";
    return {
      widthType,
      widthValue: parts[1] || "",
    };
  };

  // First try to read from TipTap's document model - check for new individual attributes
  if (editor) {
    let foundSettings: typeof defaultSettings | null = null;
    let rowIdx = 0;
    
    editor.state.doc.descendants((node) => {
      if (foundSettings) return false;
      
      if (node.type.name === "tableRow") {
        let cellIdx = 0;
        node.forEach((cell) => {
          if (cellIdx === colIndex && rowIdx === 0) {
            const attrs = cell.attrs;
            
            // Check for new individual width attributes
            const hasWidthData = ["xs", "sm", "md", "lg", "xl"].some(
              bp => attrs[`data-col-width-${bp}`]
            );
            
            if (hasWidthData) {
              const xsWidth = parseColWidth(attrs["data-col-width-xs"]);
              const smWidth = parseColWidth(attrs["data-col-width-sm"]);
              const mdWidth = parseColWidth(attrs["data-col-width-md"]);
              const lgWidth = parseColWidth(attrs["data-col-width-lg"]);
              const xlWidth = parseColWidth(attrs["data-col-width-xl"]);
              
              foundSettings = {
                columnIndex: colIndex,
                styles: {
                  xs: { ...defaultColumnConfig, ...xsWidth },
                  sm: { ...defaultColumnConfig, ...smWidth },
                  md: { ...defaultColumnConfig, ...mdWidth },
                  lg: { ...defaultColumnConfig, ...lgWidth },
                  xl: { ...defaultColumnConfig, ...xlWidth },
                },
              };
            }
            
            // Fallback to legacy data-column-settings
            if (!foundSettings) {
              const savedSettings = attrs["data-column-settings"];
            if (savedSettings) {
              try {
                const parsed = JSON.parse(savedSettings);
                if (parsed.styles) {
                  foundSettings = {
                    columnIndex: colIndex,
                    styles: parsed.styles,
                  };
                }
              } catch {
                // Fall through
                }
              }
            }
          }
          cellIdx++;
        });
        rowIdx++;
      }
    });
    
    if (foundSettings) return foundSettings;
  }

  // Fallback to DOM query
  const table = document.querySelector(".ProseMirror table");
  if (!table) return defaultSettings;

  const firstRow = table.querySelector("tr");
  if (!firstRow) return defaultSettings;

  const cells = firstRow.querySelectorAll("td, th");
  const cell = cells[colIndex] as HTMLElement;
  if (!cell) return defaultSettings;

  // Try to read from new individual width attributes first
  const hasWidthData = ["xs", "sm", "md", "lg", "xl"].some(
    bp => cell.getAttribute(`data-col-width-${bp}`)
  );
  
  if (hasWidthData) {
    const xsWidth = parseColWidth(cell.getAttribute("data-col-width-xs"));
    const smWidth = parseColWidth(cell.getAttribute("data-col-width-sm"));
    const mdWidth = parseColWidth(cell.getAttribute("data-col-width-md"));
    const lgWidth = parseColWidth(cell.getAttribute("data-col-width-lg"));
    const xlWidth = parseColWidth(cell.getAttribute("data-col-width-xl"));
    
    return {
      columnIndex: colIndex,
      styles: {
        xs: { ...defaultColumnConfig, ...xsWidth },
        sm: { ...defaultColumnConfig, ...smWidth },
        md: { ...defaultColumnConfig, ...mdWidth },
        lg: { ...defaultColumnConfig, ...lgWidth },
        xl: { ...defaultColumnConfig, ...xlWidth },
      },
    };
  }

  // Fallback to legacy data-column-settings JSON
  const savedSettings = cell.getAttribute("data-column-settings");
  if (savedSettings) {
    try {
      const parsed = JSON.parse(savedSettings);
      if (parsed.styles) {
        return {
          columnIndex: colIndex,
          styles: parsed.styles,
        };
      }
    } catch {
      // Fall through to read from styles
    }
  }

  // Read from inline styles and apply to all breakpoints
  const styles = parseStyleString(cell.style.cssText);
  const computedStyle = window.getComputedStyle(cell);

  // Normalize text-align using mapping
  const TEXT_ALIGN_NORMALIZE: Record<string, string> = {
    start: "left",
    end: "right",
  };
  let textAlign = styles["text-align"] || computedStyle.textAlign || "left";
  textAlign = TEXT_ALIGN_NORMALIZE[textAlign] || textAlign;

  // Normalize vertical-align
  const VALID_VERTICAL_ALIGN = new Set(["top", "middle", "bottom"]);
  let verticalAlign = styles["vertical-align"] || computedStyle.verticalAlign || "middle";
  if (!VALID_VERTICAL_ALIGN.has(verticalAlign)) verticalAlign = "middle";

  // Get border styles for all 4 sides
  const extractBorderWidth = (value: string | undefined): string => {
    if (!value) return "1";
    const match = value.match(/(\d+)/);
    return match ? match[1] : "1";
  };

  const BORDER_SIDES = ['top', 'right', 'bottom', 'left'] as const;
  const BORDER_STYLE_KEYS = {
    top: { color: 'border-top-color', width: 'border-top-width' },
    right: { color: 'border-right-color', width: 'border-right-width' },
    bottom: { color: 'border-bottom-color', width: 'border-bottom-width' },
    left: { color: 'border-left-color', width: 'border-left-width' },
  };

  const borderConfig = {
    top: { color: "", width: "" },
    right: { color: "", width: "" },
    bottom: { color: "", width: "" },
    left: { color: "", width: "" },
  };
  
  BORDER_SIDES.forEach(side => {
    const keys = BORDER_STYLE_KEYS[side];
    const camelCaseColor = keys.color.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    const camelCaseWidth = keys.width.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    borderConfig[side] = {
      color: rgbToHex(styles[keys.color] || (computedStyle as any)[camelCaseColor]) || "#e5e7eb",
      width: extractBorderWidth(styles[keys.width] || (computedStyle as any)[camelCaseWidth]),
    };
  });

  const padding = parsePaddingString(styles.padding || computedStyle.padding);

  // Create config from current styles
  const configFromStyles = {
    widthType: "percent" as const,
    widthValue: "33",
    textAlign: textAlign as "left" | "center" | "right",
    verticalAlign: verticalAlign as "top" | "middle" | "bottom",
    padding: padding,
    border: borderConfig,
  };

  return {
    columnIndex: colIndex,
    styles: {
      xs: { ...configFromStyles },
      sm: { ...configFromStyles },
      md: { ...configFromStyles },
      lg: { ...configFromStyles },
      xl: { ...configFromStyles },
    },
  };
};

/**
 * Find selected cell indices from ProseMirror selection
 */
export const findSelectedCellIndices = (
  editor: Editor,
  pmCells: Element[]
): number[] => {
  const selectedIndices: number[] = [];
  
  // Check for selected cells (multi-select)
  pmCells.forEach((cell, idx) => {
    if (cell.classList.contains("selectedCell")) {
      selectedIndices.push(idx);
    }
  });

  // If no multi-select, find the cell containing the cursor
  if (selectedIndices.length === 0) {
    const { $from } = editor.state.selection;
    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
        let foundIdx = 0;
        let found = false;
        editor.state.doc.descendants((n, pos) => {
          if (found) return false;
          if (n.type.name === "tableCell" || n.type.name === "tableHeader") {
            if (pos <= $from.pos && $from.pos <= pos + n.nodeSize) {
              found = true;
            } else {
              foundIdx++;
            }
          }
        });
        if (found) {
          selectedIndices.push(foundIdx);
        }
        break;
      }
    }
  }
  
  return selectedIndices;
};

/**
 * Clean up table wrapper elements for a specific table in a document
 */
export const cleanupTableWrappers = (doc: Document, tableId?: string): void => {
  if (tableId) {
    // Only remove wrapper for the specific table
    const wrapper = doc.querySelector(`.table-wrapper[data-for-table="${tableId}"]`);
    if (wrapper) {
      while (wrapper.firstChild) {
        wrapper.parentNode?.insertBefore(wrapper.firstChild, wrapper);
      }
      wrapper.remove();
    }
  } else {
    // Remove ALL table-wrapper divs (legacy behavior for compatibility)
    doc.querySelectorAll('.table-wrapper').forEach(w => {
      while (w.firstChild) {
        w.parentNode?.insertBefore(w.firstChild, w);
      }
      w.remove();
    });
  }
};

/**
 * Ensure all OTHER tables (not the one being edited) have proper wrappers
 * This prevents wrapper loss when editing one table
 */
export const ensureOtherTablesHaveWrappers = (doc: Document, currentTableId: string): void => {
  const allTables = doc.querySelectorAll('table[data-table-id]');
  
  allTables.forEach((table) => {
    const tableId = table.getAttribute('data-table-id');
    
    // Skip the table currently being edited
    if (!tableId || tableId === currentTableId) return;
    
    // Check if this OTHER table has a proper wrapper
    const parent = table.parentElement;
    const hasProperWrapper = 
      parent?.classList.contains('table-wrapper') && 
      parent?.getAttribute('data-for-table') === tableId;
    
    if (hasProperWrapper) {
      // Already has proper wrapper, skip
      return;
    }
    
    // Remove any improper/duplicate wrappers for this specific table
    let currentParent = table.parentElement;
    
    while (currentParent && currentParent.classList.contains('table-wrapper')) {
      const grandParent = currentParent.parentElement;
      if (grandParent) {
        grandParent.insertBefore(table, currentParent);
        currentParent.remove();
        currentParent = grandParent;
      } else {
        break;
      }
    }
    
    // Create proper wrapper with correct attributes
    const wrapper = createTableWrapper(doc, tableId);
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
};

/**
 * Ensure ALL tables have proper wrappers (used after inserting new tables)
 */
export const ensureAllTablesHaveWrappers = (doc: Document): void => {
  const allTables = doc.querySelectorAll('table[data-table-id]');
  
  allTables.forEach((table) => {
    const tableId = table.getAttribute('data-table-id');
    if (!tableId) return;
    
    // Check if table has a proper wrapper
    const parent = table.parentElement;
    const hasProperWrapper = 
      parent?.classList.contains('table-wrapper') && 
      parent?.getAttribute('data-for-table') === tableId;
    
    if (hasProperWrapper) {
      // Already has proper wrapper, skip
      return;
    }
    
    // Remove any improper/duplicate wrappers for this specific table
    let currentParent = table.parentElement;
    
    while (currentParent && currentParent.classList.contains('table-wrapper')) {
      const grandParent = currentParent.parentElement;
      if (grandParent) {
        grandParent.insertBefore(table, currentParent);
        currentParent.remove();
        currentParent = grandParent;
      } else {
        break;
      }
    }
    
    // Create proper wrapper with correct attributes
    const wrapper = createTableWrapper(doc, tableId);
    table.parentNode?.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
};

/**
 * Clean up table styles (remove w-full, min-width, etc.)
 */
export const cleanupTableStyles = (table: HTMLElement): void => {
  const currentClass = table.getAttribute("class") || "";
  table.setAttribute("class", currentClass.replace(/\bw-full\b/g, "").replace(/\s+/g, " ").trim());
  table.style.removeProperty("min-width");
  table.style.removeProperty("width");
  table.style.removeProperty("max-width");

  // Clean up col elements
  table.querySelectorAll("col").forEach((col) => {
    const colEl = col as HTMLElement;
    colEl.style.removeProperty("min-width");
    colEl.style.removeProperty("width");
    colEl.style.removeProperty("max-width");
    const colStyle = colEl.getAttribute("style");
    if (!colStyle || colStyle.trim() === "") {
      colEl.removeAttribute("style");
    }
  });
};

/**
 * Create a table wrapper element
 */
export const createTableWrapper = (doc: Document, tableId: string): HTMLDivElement => {
  const wrapper = doc.createElement("div");
  wrapper.className = "table-wrapper";
  wrapper.setAttribute("data-for-table", tableId);
  wrapper.setAttribute("data-table-wrapper", "true");
  wrapper.style.cssText = "width: 100%; max-width: 100%;";
  return wrapper;
};

